import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { AuthService } from '../core/auth.service';

interface ExportPreview {
  count: number;
  sample: Array<{
    primary_guest?: { name_first?: string; name_last?: string } | null;
    arrival_date?: string | null;
    departure_date?: string | null;
    room_number?: string | null;
    reservation_status_pms?: string | null;
    pms_reservation_id?: string | null;
    pms_confirmation_code?: string | null;
  }>;
}

@Component({
  selector: 'app-reservation-export',
  imports: [FormsModule],
  template: `
    <section class="card">
      <header class="page-head">
        <div>
          <h1>Export VM Reservations</h1>
          <p class="muted">Pull PMS reservations from the Ops Management reservation endpoint and download a CSV.</p>
        </div>
      </header>

      @if (!isAdmin()) {
        <div class="state error">Admin role required.</div>
      } @else {
        @if (message()) {
          <div class="flash" [class.flash--error]="error()">{{ message() }}</div>
        }

        <form class="filters" (submit)="preview($event)">
          <label><span>Chain Code</span><input [(ngModel)]="chaincode" name="chaincode" maxlength="50" required></label>
          <label><span>Property Code</span><input [(ngModel)]="propertycode" name="propertycode" maxlength="50" required></label>
          <label><span>From Date</span><input [(ngModel)]="fromdate" name="fromdate" type="date" required></label>
          <label><span>To Date</span><input [(ngModel)]="todate" name="todate" type="date" required></label>
          <label><span>Ops API ID</span><input [(ngModel)]="apiId" name="apiId" maxlength="200" autocomplete="off" required></label>
          <label><span>Ops API Key</span><input [(ngModel)]="apiKey" name="apiKey" type="password" maxlength="500" autocomplete="off" required></label>
          <div class="filter-actions">
            <button type="submit" [disabled]="loading()">Preview</button>
            <button type="button" class="secondary" (click)="downloadCsv()" [disabled]="loading() || !previewData()">Download CSV</button>
          </div>
        </form>

        @if (loading()) {
          <div class="state">Loading reservations from Ops Management…</div>
        } @else if (previewData()) {
          <div class="summary">
            <strong>{{ previewData()?.count ?? 0 }}</strong>
            <span>reservations found for {{ chaincode }}/{{ propertycode }} from {{ fromdate }} to {{ todate }}</span>
          </div>

          @if ((previewData()?.sample?.length ?? 0) > 0) {
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>PMS Res ID</th>
                    <th>Confirmation</th>
                    <th>Guest</th>
                    <th>Arrival</th>
                    <th>Departure</th>
                    <th>Room</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  @for (r of previewData()?.sample; track r.pms_reservation_id || r.pms_confirmation_code || $index) {
                    <tr>
                      <td>{{ r.pms_reservation_id || '' }}</td>
                      <td>{{ r.pms_confirmation_code || '' }}</td>
                      <td>{{ r.primary_guest?.name_first || '' }} {{ r.primary_guest?.name_last || '' }}</td>
                      <td>{{ dateOnly(r.arrival_date) }}</td>
                      <td>{{ dateOnly(r.departure_date) }}</td>
                      <td>{{ r.room_number || '' }}</td>
                      <td><span class="badge">{{ r.reservation_status_pms || '' }}</span></td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="state">No reservations matched this range.</div>
          }
        }
      }
    </section>
  `,
  styles: `
    .card{background:#fff;border:1px solid var(--line);border-radius:14px;padding:16px;display:grid;gap:14px}
    .page-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;flex-wrap:wrap}
    h1{margin:0}
    .muted{margin:4px 0 0;color:var(--muted)}
    .filters{display:grid;grid-template-columns:repeat(3,minmax(0,1fr)) auto;gap:10px;align-items:end}
    label{display:grid;gap:6px}
    label span{font-size:12px;color:#4e5c75;text-transform:uppercase;letter-spacing:.04em}
    input{height:40px;border:1px solid #c8d2e1;border-radius:10px;padding:0 12px}
    button{height:38px;border:0;border-radius:10px;background:var(--primary);color:var(--primary-ink);font-weight:700;cursor:pointer;padding:0 12px}
    button:disabled{opacity:.6;cursor:not-allowed}
    .secondary{background:#6b778c}
    .filter-actions{display:flex;gap:8px;flex-wrap:wrap}
    .flash{padding:10px;border-radius:8px;background:#eef4ff;color:#0a2f6b;border:1px solid #c7d7f5}
    .flash--error{background:#fff1f1;color:#8a1f1f;border-color:#f0caca}
    .state{padding:18px;border:1px dashed var(--line);border-radius:12px;color:var(--muted);background:#f8fafc}
    .state.error{color:#8a1f1f;background:#fff7f7;border-color:#f0caca}
    .summary{display:flex;gap:8px;align-items:baseline;padding:12px;border:1px solid #d5deed;border-radius:12px;background:#f4f7fc;color:#24416a}
    .summary strong{font-size:1.5rem}
    .table-wrap{overflow:auto}
    table{width:100%;border-collapse:collapse;min-width:860px}
    th,td{padding:10px;border-bottom:1px solid #edf0f6;text-align:left;vertical-align:top}
    th{font-size:12px;text-transform:uppercase;letter-spacing:.4px;color:#4e5c75}
    .badge{display:inline-block;padding:2px 8px;border:1px solid #d5deed;border-radius:999px;background:#f4f7fc;font-size:12px}
    @media (max-width: 980px){ .filters{grid-template-columns:1fr 1fr} }
    @media (max-width: 720px){ .filters{grid-template-columns:1fr} }
  `,
})
export class ReservationExportComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  readonly loading = signal(false);
  readonly message = signal('');
  readonly error = signal(false);
  readonly previewData = signal<ExportPreview | null>(null);

  chaincode = '';
  propertycode = '';
  fromdate = new Date().toISOString().slice(0, 10);
  todate = new Date().toISOString().slice(0, 10);
  apiId = '';
  apiKey = '';

  async ngOnInit(): Promise<void> {
    try {
      const [settings, ctx] = await Promise.all([
        firstValueFrom(this.api.get<{ settings: { ChainCode?: string; HotelCode?: string } }>('/settings')),
        firstValueFrom(this.api.get<{ activeProperty: { code: string } | null }>('/context/property')),
      ]);
      this.chaincode = String(settings.settings?.ChainCode || '').trim();
      this.propertycode = String(ctx.activeProperty?.code || settings.settings?.HotelCode || '').trim();
    } catch {
      this.message.set('Unable to load default export criteria.');
      this.error.set(true);
    }
  }

  isAdmin(): boolean {
    return String(this.auth.user()?.role ?? '').toLowerCase() === 'admin';
  }

  async preview(event: Event): Promise<void> {
    event.preventDefault();
    this.message.set('');
    this.error.set(false);
    this.loading.set(true);
    try {
      const data = await firstValueFrom(
        this.api.post<ExportPreview>('/reservation-export/preview', {
          chaincode: this.chaincode.trim(),
          propertycode: this.propertycode.trim(),
          fromdate: this.fromdate,
          todate: this.todate,
          apiId: this.apiId.trim(),
          apiKey: this.apiKey,
        }),
      );
      this.previewData.set(data);
    } catch (err) {
      this.previewData.set(null);
      this.message.set(this.describeError(err));
      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  downloadCsv(): void {
    this.message.set('');
    this.error.set(false);
    window.location.href = `/api/reservation-export/csv?${this.params().toString()}`;
  }

  dateOnly(value: string | null | undefined): string {
    return String(value ?? '').slice(0, 10);
  }

  private params(): URLSearchParams {
    return new URLSearchParams({
      chaincode: this.chaincode.trim(),
      propertycode: this.propertycode.trim(),
      fromdate: this.fromdate,
      todate: this.todate,
    });
  }

  private describeError(err: unknown): string {
    const candidate = err as { error?: { message?: string }; message?: string; status?: number };
    return candidate?.error?.message || candidate?.message || `Reservation export failed${candidate?.status ? ` (HTTP ${candidate.status})` : ''}.`;
  }
}
